import React from 'react';
import { Layers, TrendingUp, RefreshCw, Cloud, Calendar, BarChart3, Shield, Zap, Check, ArrowRight, DollarSign, Target, PieChart } from 'lucide-react';

// Same color system as the main app
const colors = {
  bgPrimary: '#0a0e17',
  bgSecondary: '#0d1117',
  bgCard: '#0f1419',
  bgCardHover: '#141a22',
  bgTertiary: '#1a1f2e',
  borderSubtle: 'rgba(148, 163, 184, 0.1)',
  borderDefault: 'rgba(148, 163, 184, 0.15)',
  textPrimary: '#f1f5f9',
  textSecondary: '#94a3b8',
  textTertiary: '#64748b',
  accent: '#06b6d4',
  accentHover: '#22d3ee',
  purple: '#a855f7',
  profit: '#10b981',
  profitMuted: 'rgba(16, 185, 129, 0.15)',
};

const LandingPage = ({ onGetStarted }) => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div style={styles.container}>
      {/* Navigation */}
      <nav style={styles.nav}>
        <div style={styles.navContent}>
          <div style={styles.logo}>
            <div style={styles.logoIcon}>
              <Layers size={24} />
            </div>
            <span style={styles.logoText}>WheelStrat</span>
          </div>
          <div style={styles.navLinks}>
            <button style={styles.navLink} onClick={() => scrollToSection('features')}>Features</button>
            <button style={styles.navLink} onClick={() => scrollToSection('benefits')}>Benefits</button>
            <button style={styles.navLink} onClick={() => scrollToSection('pricing')}>Pricing</button>
          </div>
          <div style={styles.navButtons}>
            <button style={styles.signInButton} onClick={onGetStarted}>Sign In</button>
            <button style={styles.getStartedButtonSmall} onClick={onGetStarted}>
              Get Started
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <div style={styles.heroText}>
            <div style={styles.badge}>
              <Zap size={14} />
              <span>Built for Theta Gang</span>
            </div>
            <h1 style={styles.heroTitle}>
              Track Your Wheel Strategy
              <span style={styles.heroTitleAccent}> Like a Pro</span>
            </h1>
            <p style={styles.heroSubtitle}>
              The premium journal for options traders who sell CSPs, collect premium, 
              and run covered calls. Track every trade, analyze performance, and sync across all your devices.
            </p>
            <div style={styles.heroCTA}>
              <button style={styles.primaryButton} onClick={onGetStarted}>
                Get Started — It's Free
                <ArrowRight size={18} />
              </button>
              <p style={styles.ctaNote}>No credit card required</p>
            </div>
            <div style={styles.heroStats}>
              <div style={styles.heroStat}>
                <span style={styles.heroStatValue}>100%</span>
                <span style={styles.heroStatLabel}>Free</span>
              </div>
              <div style={styles.heroStatDivider} />
              <div style={styles.heroStat}>
                <span style={styles.heroStatValue}>∞</span>
                <span style={styles.heroStatLabel}>Unlimited Trades</span>
              </div>
              <div style={styles.heroStatDivider} />
              <div style={styles.heroStat}>
                <span style={styles.heroStatValue}><Cloud size={20} /></span>
                <span style={styles.heroStatLabel}>Cloud Sync</span>
              </div>
            </div>
          </div>
          <div style={styles.heroImage}>
            <div style={styles.mockupContainer}>
              <div style={styles.mockupWindow}>
                <div style={styles.mockupHeader}>
                  <div style={styles.mockupDots}>
                    <div style={{...styles.mockupDot, background: '#ff5f57'}} />
                    <div style={{...styles.mockupDot, background: '#ffbd2e'}} />
                    <div style={{...styles.mockupDot, background: '#28ca41'}} />
                  </div>
                </div>
                <div style={styles.mockupContent}>
                  {/* Mini Dashboard Preview */}
                  <div style={styles.mockupStats}>
                    <div style={styles.mockupStatCard}>
                      <span style={styles.mockupStatLabel}>Total P/L</span>
                      <span style={{...styles.mockupStatValue, color: colors.profit}}>+$4,250</span>
                    </div>
                    <div style={styles.mockupStatCard}>
                      <span style={styles.mockupStatLabel}>Win Rate</span>
                      <span style={styles.mockupStatValue}>78%</span>
                    </div>
                    <div style={styles.mockupStatCard}>
                      <span style={styles.mockupStatLabel}>Open</span>
                      <span style={styles.mockupStatValue}>5</span>
                    </div>
                  </div>
                  <div style={styles.mockupTrades}>
                    <div style={styles.mockupTrade}>
                      <div style={styles.mockupTicker}>SPY</div>
                      <div style={styles.mockupType}>CSP $450</div>
                      <div style={{...styles.mockupPL, color: colors.profit}}>+$125</div>
                    </div>
                    <div style={styles.mockupTrade}>
                      <div style={styles.mockupTicker}>AAPL</div>
                      <div style={styles.mockupType}>CC $180</div>
                      <div style={{...styles.mockupPL, color: colors.profit}}>+$89</div>
                    </div>
                    <div style={styles.mockupTrade}>
                      <div style={styles.mockupTicker}>NVDA</div>
                      <div style={styles.mockupType}>CSP $420</div>
                      <div style={{...styles.mockupPL, color: colors.accent}}>Open</div>
                    </div>
                  </div>
                </div>
              </div>
              <div style={styles.mockupGlow} />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" style={styles.features}>
        <div style={styles.sectionContent}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Everything You Need</h2>
            <p style={styles.sectionSubtitle}>
              Powerful features designed specifically for wheel strategy traders
            </p>
          </div>
          <div style={styles.featureGrid}>
            <div style={styles.featureCard}>
              <div style={styles.featureIcon}>
                <DollarSign size={24} />
              </div>
              <h3 style={styles.featureTitle}>Track Every Trade</h3>
              <p style={styles.featureDesc}>
                Log CSPs, Covered Calls, and Shares with all the details. Quick entry for fast logging.
              </p>
            </div>
            <div style={styles.featureCard}>
              <div style={styles.featureIcon}>
                <TrendingUp size={24} />
              </div>
              <h3 style={styles.featureTitle}>P/L Analytics</h3>
              <p style={styles.featureDesc}>
                See your true profit/loss with fees included. Track win rate, weekly performance, and more.
              </p>
            </div>
            <div style={styles.featureCard}>
              <div style={styles.featureIcon}>
                <RefreshCw size={24} />
              </div>
              <h3 style={styles.featureTitle}>Roll Chain Tracking</h3>
              <p style={styles.featureDesc}>
                Track rolled positions with a visual timeline. See total premium collected across all rolls.
              </p>
            </div>
            <div style={styles.featureCard}>
              <div style={styles.featureIcon}>
                <Calendar size={24} />
              </div>
              <h3 style={styles.featureTitle}>Expiration Calendar</h3>
              <p style={styles.featureDesc}>
                Visual calendar showing all your expiring positions. Never miss an expiration date again.
              </p>
            </div>
            <div style={styles.featureCard}>
              <div style={styles.featureIcon}>
                <Cloud size={24} />
              </div>
              <h3 style={styles.featureTitle}>Cloud Sync</h3>
              <p style={styles.featureDesc}>
                Your data syncs instantly across all devices. Access your trades from anywhere.
              </p>
            </div>
            <div style={styles.featureCard}>
              <div style={styles.featureIcon}>
                <Shield size={24} />
              </div>
              <h3 style={styles.featureTitle}>Secure & Private</h3>
              <p style={styles.featureDesc}>
                Google Sign-In authentication. Your data is encrypted and only accessible by you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" style={styles.benefits}>
        <div style={styles.sectionContent}>
          <div style={styles.benefitsGrid}>
            <div style={styles.benefitsText}>
              <h2 style={styles.sectionTitle}>Built for The Wheel Strategy</h2>
              <p style={styles.benefitsSubtitle}>
                Most trading journals are generic. WheelStrat is designed specifically for 
                options traders running the wheel.
              </p>
              <ul style={styles.benefitsList}>
                <li style={styles.benefitItem}>
                  <div style={styles.benefitCheck}><Check size={16} /></div>
                  <span>Track the full wheel cycle: CSP → Assignment → Shares → CC</span>
                </li>
                <li style={styles.benefitItem}>
                  <div style={styles.benefitCheck}><Check size={16} /></div>
                  <span>One-click actions: Close, Expire, Roll, Assign</span>
                </li>
                <li style={styles.benefitItem}>
                  <div style={styles.benefitCheck}><Check size={16} /></div>
                  <span>Automatic P/L calculation including fees</span>
                </li>
                <li style={styles.benefitItem}>
                  <div style={styles.benefitCheck}><Check size={16} /></div>
                  <span>DTE countdown with color-coded warnings</span>
                </li>
                <li style={styles.benefitItem}>
                  <div style={styles.benefitCheck}><Check size={16} /></div>
                  <span>Bulk import from CSV for existing trades</span>
                </li>
                <li style={styles.benefitItem}>
                  <div style={styles.benefitCheck}><Check size={16} /></div>
                  <span>Export your data anytime — you own your data</span>
                </li>
              </ul>
            </div>
            <div style={styles.benefitsVisual}>
              <div style={styles.benefitsCard}>
                <div style={styles.benefitsCardHeader}>
                  <Target size={20} style={{ color: colors.accent }} />
                  <span>The Wheel Cycle</span>
                </div>
                <div style={styles.wheelCycle}>
                  <div style={styles.wheelStep}>
                    <div style={{...styles.wheelStepIcon, background: colors.profitMuted, color: colors.profit}}>1</div>
                    <div style={styles.wheelStepText}>
                      <span style={styles.wheelStepTitle}>Sell CSP</span>
                      <span style={styles.wheelStepDesc}>Collect premium</span>
                    </div>
                  </div>
                  <div style={styles.wheelArrow}>↓</div>
                  <div style={styles.wheelStep}>
                    <div style={{...styles.wheelStepIcon, background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b'}}>2</div>
                    <div style={styles.wheelStepText}>
                      <span style={styles.wheelStepTitle}>Get Assigned</span>
                      <span style={styles.wheelStepDesc}>Buy shares at strike</span>
                    </div>
                  </div>
                  <div style={styles.wheelArrow}>↓</div>
                  <div style={styles.wheelStep}>
                    <div style={{...styles.wheelStepIcon, background: 'rgba(168, 85, 247, 0.15)', color: colors.purple}}>3</div>
                    <div style={styles.wheelStepText}>
                      <span style={styles.wheelStepTitle}>Sell CC</span>
                      <span style={styles.wheelStepDesc}>More premium</span>
                    </div>
                  </div>
                  <div style={styles.wheelArrow}>↓</div>
                  <div style={styles.wheelStep}>
                    <div style={{...styles.wheelStepIcon, background: 'rgba(6, 182, 212, 0.15)', color: colors.accent}}>4</div>
                    <div style={styles.wheelStepText}>
                      <span style={styles.wheelStepTitle}>Shares Called</span>
                      <span style={styles.wheelStepDesc}>Repeat the wheel</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" style={styles.pricing}>
        <div style={styles.sectionContent}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Simple Pricing</h2>
            <p style={styles.sectionSubtitle}>
              Everything you need, completely free. No hidden fees, no premium tiers.
            </p>
          </div>
          <div style={styles.pricingCard}>
            <div style={styles.pricingBadge}>FOREVER FREE</div>
            <div style={styles.pricingPrice}>
              <span style={styles.pricingAmount}>$0</span>
              <span style={styles.pricingPeriod}>/month</span>
            </div>
            <p style={styles.pricingDesc}>
              Full access to all features. No credit card required.
            </p>
            <ul style={styles.pricingFeatures}>
              <li style={styles.pricingFeature}>
                <Check size={18} style={{ color: colors.profit }} />
                <span>Unlimited trades</span>
              </li>
              <li style={styles.pricingFeature}>
                <Check size={18} style={{ color: colors.profit }} />
                <span>Cloud sync across devices</span>
              </li>
              <li style={styles.pricingFeature}>
                <Check size={18} style={{ color: colors.profit }} />
                <span>Full analytics dashboard</span>
              </li>
              <li style={styles.pricingFeature}>
                <Check size={18} style={{ color: colors.profit }} />
                <span>Roll chain tracking</span>
              </li>
              <li style={styles.pricingFeature}>
                <Check size={18} style={{ color: colors.profit }} />
                <span>Expiration calendar</span>
              </li>
              <li style={styles.pricingFeature}>
                <Check size={18} style={{ color: colors.profit }} />
                <span>CSV import & export</span>
              </li>
              <li style={styles.pricingFeature}>
                <Check size={18} style={{ color: colors.profit }} />
                <span>Secure Google Sign-In</span>
              </li>
            </ul>
            <button style={styles.pricingButton} onClick={onGetStarted}>
              Get Started Now
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={styles.finalCTA}>
        <div style={styles.sectionContent}>
          <div style={styles.ctaBox}>
            <h2 style={styles.ctaTitle}>Ready to Track Your Premium?</h2>
            <p style={styles.ctaSubtitle}>
              Join traders who use WheelStrat to manage their wheel strategy.
            </p>
            <button style={styles.ctaButton} onClick={onGetStarted}>
              Start Tracking — Free Forever
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div style={styles.footerLogo}>
            <div style={styles.logoIcon}>
              <Layers size={20} />
            </div>
            <span style={styles.logoText}>WheelStrat</span>
          </div>
          <div style={styles.footerLinks}>
            <a href="mailto:support@wheelstrat.app" style={styles.footerLink}>Contact</a>
            <span style={styles.footerDivider}>•</span>
            <button style={styles.footerLink} onClick={() => scrollToSection('features')}>Features</button>
            <span style={styles.footerDivider}>•</span>
            <button style={styles.footerLink} onClick={() => scrollToSection('pricing')}>Pricing</button>
          </div>
          <p style={styles.footerCopy}>© 2026 WheelStrat. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

// Styles
const styles = {
  container: {
    minHeight: '100vh',
    background: colors.bgPrimary,
    color: colors.textPrimary,
    fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },

  // Navigation
  nav: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    background: 'rgba(10, 14, 23, 0.8)',
    backdropFilter: 'blur(20px)',
    borderBottom: `1px solid ${colors.borderSubtle}`,
  },
  navContent: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '16px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  logoIcon: {
    width: 40,
    height: 40,
    background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.purple} 100%)`,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
  },
  logoText: {
    fontSize: 20,
    fontWeight: 700,
    color: colors.textPrimary,
  },
  navLinks: {
    display: 'flex',
    gap: 32,
  },
  navLink: {
    background: 'none',
    border: 'none',
    color: colors.textSecondary,
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'color 0.2s',
  },
  navButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  signInButton: {
    background: 'none',
    border: 'none',
    color: colors.textSecondary,
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
    padding: '8px 16px',
  },
  getStartedButtonSmall: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.purple} 100%)`,
    border: 'none',
    borderRadius: 8,
    color: 'white',
    fontSize: 14,
    fontWeight: 600,
    padding: '10px 20px',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },

  // Hero
  hero: {
    paddingTop: 120,
    paddingBottom: 80,
    background: `radial-gradient(ellipse at top, rgba(6, 182, 212, 0.1) 0%, transparent 50%)`,
  },
  heroContent: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 32px',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 64,
    alignItems: 'center',
  },
  heroText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: colors.bgTertiary,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 100,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    color: colors.accent,
    width: 'fit-content',
  },
  heroTitle: {
    fontSize: 56,
    fontWeight: 700,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    margin: 0,
    color: colors.textPrimary,
  },
  heroTitleAccent: {
    background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.purple} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  heroSubtitle: {
    fontSize: 18,
    lineHeight: 1.6,
    color: colors.textSecondary,
    margin: 0,
  },
  heroCTA: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  primaryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.purple} 100%)`,
    border: 'none',
    borderRadius: 12,
    color: 'white',
    fontSize: 16,
    fontWeight: 600,
    padding: '16px 32px',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    width: 'fit-content',
    boxShadow: `0 8px 32px ${colors.accent}40`,
  },
  ctaNote: {
    fontSize: 13,
    color: colors.textTertiary,
    margin: 0,
  },
  heroStats: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    marginTop: 16,
  },
  heroStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  heroStatValue: {
    fontSize: 24,
    fontWeight: 700,
    color: colors.textPrimary,
    display: 'flex',
    alignItems: 'center',
  },
  heroStatLabel: {
    fontSize: 13,
    color: colors.textTertiary,
  },
  heroStatDivider: {
    width: 1,
    height: 40,
    background: colors.borderDefault,
  },

  // Hero Image/Mockup
  heroImage: {
    display: 'flex',
    justifyContent: 'center',
  },
  mockupContainer: {
    position: 'relative',
  },
  mockupWindow: {
    background: colors.bgCard,
    borderRadius: 16,
    border: `1px solid ${colors.borderDefault}`,
    overflow: 'hidden',
    width: 480,
    boxShadow: '0 24px 80px rgba(0, 0, 0, 0.4)',
  },
  mockupHeader: {
    background: colors.bgSecondary,
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.borderSubtle}`,
  },
  mockupDots: {
    display: 'flex',
    gap: 8,
  },
  mockupDot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
  },
  mockupContent: {
    padding: 20,
  },
  mockupStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12,
    marginBottom: 16,
  },
  mockupStatCard: {
    background: colors.bgTertiary,
    borderRadius: 10,
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  mockupStatLabel: {
    fontSize: 11,
    color: colors.textTertiary,
  },
  mockupStatValue: {
    fontSize: 18,
    fontWeight: 700,
    color: colors.textPrimary,
  },
  mockupTrades: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  mockupTrade: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: colors.bgTertiary,
    borderRadius: 10,
    padding: '12px 16px',
  },
  mockupTicker: {
    fontWeight: 700,
    fontSize: 14,
  },
  mockupType: {
    fontSize: 13,
    color: colors.textSecondary,
  },
  mockupPL: {
    fontWeight: 600,
    fontSize: 14,
  },
  mockupGlow: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '120%',
    height: '120%',
    background: `radial-gradient(ellipse, ${colors.accent}20 0%, transparent 70%)`,
    zIndex: -1,
    pointerEvents: 'none',
  },

  // Features
  features: {
    padding: '100px 0',
    background: colors.bgSecondary,
  },
  sectionContent: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 32px',
  },
  sectionHeader: {
    textAlign: 'center',
    marginBottom: 64,
  },
  sectionTitle: {
    fontSize: 40,
    fontWeight: 700,
    margin: '0 0 16px 0',
    letterSpacing: '-0.02em',
  },
  sectionSubtitle: {
    fontSize: 18,
    color: colors.textSecondary,
    margin: 0,
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 24,
  },
  featureCard: {
    background: colors.bgCard,
    border: `1px solid ${colors.borderSubtle}`,
    borderRadius: 16,
    padding: 32,
    transition: 'border-color 0.2s, transform 0.2s',
  },
  featureIcon: {
    width: 48,
    height: 48,
    background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.purple}20 100%)`,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.accent,
    marginBottom: 20,
  },
  featureTitle: {
    fontSize: 18,
    fontWeight: 600,
    margin: '0 0 12px 0',
  },
  featureDesc: {
    fontSize: 15,
    color: colors.textSecondary,
    lineHeight: 1.6,
    margin: 0,
  },

  // Benefits
  benefits: {
    padding: '100px 0',
  },
  benefitsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 80,
    alignItems: 'center',
  },
  benefitsText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  benefitsSubtitle: {
    fontSize: 18,
    color: colors.textSecondary,
    lineHeight: 1.6,
    margin: 0,
  },
  benefitsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  benefitItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    fontSize: 16,
    color: colors.textSecondary,
  },
  benefitCheck: {
    width: 24,
    height: 24,
    background: colors.profitMuted,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.profit,
    flexShrink: 0,
  },
  benefitsVisual: {
    display: 'flex',
    justifyContent: 'center',
  },
  benefitsCard: {
    background: colors.bgCard,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 20,
    padding: 32,
    width: '100%',
    maxWidth: 400,
  },
  benefitsCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
    fontSize: 18,
    fontWeight: 600,
  },
  wheelCycle: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  wheelStep: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: 12,
    background: colors.bgTertiary,
    borderRadius: 12,
  },
  wheelStepIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 14,
  },
  wheelStepText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  wheelStepTitle: {
    fontWeight: 600,
    fontSize: 14,
  },
  wheelStepDesc: {
    fontSize: 12,
    color: colors.textTertiary,
  },
  wheelArrow: {
    textAlign: 'center',
    color: colors.textTertiary,
    fontSize: 18,
  },

  // Pricing
  pricing: {
    padding: '100px 0',
    background: colors.bgSecondary,
  },
  pricingCard: {
    maxWidth: 480,
    margin: '0 auto',
    background: colors.bgCard,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 24,
    padding: 48,
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  pricingBadge: {
    position: 'absolute',
    top: 24,
    right: -32,
    background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.purple} 100%)`,
    color: 'white',
    fontSize: 11,
    fontWeight: 700,
    padding: '6px 40px',
    transform: 'rotate(45deg)',
  },
  pricingPrice: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: 4,
    marginBottom: 8,
  },
  pricingAmount: {
    fontSize: 64,
    fontWeight: 700,
    color: colors.textPrimary,
  },
  pricingPeriod: {
    fontSize: 18,
    color: colors.textTertiary,
  },
  pricingDesc: {
    fontSize: 16,
    color: colors.textSecondary,
    marginBottom: 32,
  },
  pricingFeatures: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 32px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    textAlign: 'left',
  },
  pricingFeature: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontSize: 15,
    color: colors.textSecondary,
  },
  pricingButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.purple} 100%)`,
    border: 'none',
    borderRadius: 12,
    color: 'white',
    fontSize: 16,
    fontWeight: 600,
    padding: '16px 32px',
    cursor: 'pointer',
    width: '100%',
    boxShadow: `0 8px 32px ${colors.accent}40`,
  },

  // Final CTA
  finalCTA: {
    padding: '100px 0',
  },
  ctaBox: {
    background: `linear-gradient(135deg, ${colors.accent}10 0%, ${colors.purple}10 100%)`,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 24,
    padding: '64px 48px',
    textAlign: 'center',
  },
  ctaTitle: {
    fontSize: 36,
    fontWeight: 700,
    margin: '0 0 16px 0',
  },
  ctaSubtitle: {
    fontSize: 18,
    color: colors.textSecondary,
    margin: '0 0 32px 0',
  },
  ctaButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.purple} 100%)`,
    border: 'none',
    borderRadius: 12,
    color: 'white',
    fontSize: 16,
    fontWeight: 600,
    padding: '16px 32px',
    cursor: 'pointer',
    boxShadow: `0 8px 32px ${colors.accent}40`,
  },

  // Footer
  footer: {
    borderTop: `1px solid ${colors.borderSubtle}`,
    padding: '48px 0',
  },
  footerContent: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 24,
  },
  footerLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  footerLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  footerLink: {
    background: 'none',
    border: 'none',
    color: colors.textTertiary,
    fontSize: 14,
    cursor: 'pointer',
    textDecoration: 'none',
  },
  footerDivider: {
    color: colors.textTertiary,
  },
  footerCopy: {
    fontSize: 13,
    color: colors.textTertiary,
    margin: 0,
  },
};

export default LandingPage;